import { useState, useCallback } from 'react'
import { Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { OrderFilterBar } from './components/OrderFilterBar'
import { OrderTable } from './components/OrderTable'
import { OrderDetailDrawer } from './components/OrderDetailDrawer'
import { DeliveryDialog } from './components/DeliveryDialog'
import { useSupplierOrders, useSupplierOrder, useSupplierOrderStatus, useOrderDelivery, useOrderSplitDelivery } from '@/hooks/useSupplierOrder'
import { showError } from '@/lib/error'
import { type OrderListParams, type Order, type DeliveryParams, type SplitDeliveryParams } from '@/schemas/supplier'

export function SupplierOrdersPage() {
  const [params, setParams] = useState<OrderListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })
  const [deliveryOrder, setDeliveryOrder] = useState<Order | null>(null)
  const [detailOrderId, setDetailOrderId] = useState<number | null>(null)

  // 查询订单列表
  const { data, isLoading } = useSupplierOrders(params)

  // 查询订单详情
  const { data: detailOrder, isLoading: isDetailLoading } = useSupplierOrder(
    detailOrderId ?? 0,
    detailOrderId !== null
  )

  // 查询订单状态记录
  const { data: orderStatusList, isLoading: isStatusLoading } = useSupplierOrderStatus(
    detailOrderId ?? 0,
    detailOrderId !== null
  )

  // 发货 mutation
  const deliveryMutation = useOrderDelivery()
  const splitDeliveryMutation = useOrderSplitDelivery()

  // 更新筛选参数
  const handleParamsChange = useCallback((newParams: Partial<OrderListParams>) => {
    setParams((prev) => ({
      ...prev,
      ...newParams,
      page: 1, // 筛选条件变化时重置页码
    }))
  }, [])

  // 处理查看详情
  const handleViewDetail = useCallback((order: Order) => {
    setDetailOrderId(order.id)
  }, [])

  // 处理发货
  const handleDelivery = useCallback((order: Order) => {
    setDeliveryOrder(order)
  }, [])

  // 确认发货（从列表页发货对话框）
  const handleDeliveryConfirm = useCallback((data: DeliveryParams | SplitDeliveryParams) => {
    if (!deliveryOrder) return

    if ('cart_ids' in data) {
      splitDeliveryMutation.mutate(
        { id: deliveryOrder.id, data },
        {
          onSuccess: () => setDeliveryOrder(null),
          onError: (error) => showError(error, '发货失败'),
        }
      )
    } else {
      deliveryMutation.mutate(
        { id: deliveryOrder.id, data },
        {
          onSuccess: () => setDeliveryOrder(null),
          onError: (error) => showError(error, '发货失败'),
        }
      )
    }
  }, [deliveryOrder, deliveryMutation, splitDeliveryMutation])

  // 确认发货（从详情抽屉发货）
  const handleDrawerDelivery = useCallback((orderId: number, data: DeliveryParams | SplitDeliveryParams) => {
    if ('cart_ids' in data) {
      splitDeliveryMutation.mutate(
        { id: orderId, data },
        {
          onSuccess: () => setDetailOrderId(null),
          onError: (error) => showError(error, '发货失败'),
        }
      )
    } else {
      deliveryMutation.mutate(
        { id: orderId, data },
        {
          onSuccess: () => setDetailOrderId(null),
          onError: (error) => showError(error, '发货失败'),
        }
      )
    }
  }, [deliveryMutation, splitDeliveryMutation])

  // 分页处理
  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">供应商订单</h1>
          <p className="text-muted-foreground">管理供应商订单和发货</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Package className="mr-1.5 h-4 w-4" />
            导出订单
          </Button>
        </div>
      </div>

      {/* 筛选栏 */}
      <OrderFilterBar params={params} onParamsChange={handleParamsChange} />

      {/* 订单表格 */}
      <OrderTable
        orders={data?.list || []}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onDelivery={handleDelivery}
      />

      {/* 分页 */}
      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* 发货对话框 */}
      {deliveryOrder && (
        <DeliveryDialog
          order={deliveryOrder}
          open={!!deliveryOrder}
          onOpenChange={(open) => !open && setDeliveryOrder(null)}
          onConfirm={handleDeliveryConfirm}
          isLoading={deliveryMutation.isPending || splitDeliveryMutation.isPending}
        />
      )}

      {/* 订单详情抽屉 */}
      <OrderDetailDrawer
        order={isDetailLoading ? null : detailOrder ?? null}
        open={detailOrderId !== null}
        onOpenChange={(open) => !open && setDetailOrderId(null)}
        onDelivery={handleDrawerDelivery}
        isDeliveryPending={deliveryMutation.isPending || splitDeliveryMutation.isPending}
        orderStatusList={orderStatusList}
        isStatusLoading={isStatusLoading}
      />
    </div>
  )
}

export { OrderFilterBar } from './components/OrderFilterBar'
export { OrderTable } from './components/OrderTable'
export { DeliveryDialog } from './components/DeliveryDialog'
